import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import assert from 'assert';
import React, {ReactElement, useState} from 'react';

import {isSchemaTag, Tag} from 'folder-items/FileConstants';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import TagValueViewer from 'metadata/TagValueViewer';
import CreateTestOrdersButton from 'sample-tests/ui/test-orders/buttons/CreateTestOrdersButton';
import {TestOrderStatusDisplay} from 'sample-tests/ui/utils/TestOrderStatusUtil';
import {ChipSize} from 'ui-library/chips/Chip';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import {Cell, CellProps, Column, Table} from 'ui-library/simple-table/SimpleTable';
import SortableHeaderCell from 'ui-library/simple-table/SortableHeaderCell';
import useSimpleTableSortState, {SimpleTableSortState} from 'ui-library/simple-table/useSimpleTableSortState';
import sortedBy from 'util/sortedBy';

import {
  renderTestOrderAssayChip,
  renderTestOrderContainerChip,
  renderTestOrderTestDefinitionChip,
  renderTestOrderWorksheetChip,
} from '../utils/testOrderChipsUtil';
import {TestOrdersViewTableTestOrderFragment as TestOrderGQL} from './__generated/TestOrdersViewTableTestOrderFragment';

interface HeaderInfo {
  name: string;
  propertyKey:
    | 'name'
    | 'testDefinitionName'
    | 'status'
    | 'dilution'
    | 'analysisComment'
    | 'container'
    | 'assay'
    | 'worksheet'
    | `result_${string}`;
  sortIndex: number;
}

const NOT_SORTABLE = -1;
const HEADER_COLUMNS: {[index: string]: HeaderInfo} = {
  testOrder: {
    name: 'Test order',
    propertyKey: 'name',
    sortIndex: 0,
  },
  status: {
    name: 'Status',
    propertyKey: 'status',
    sortIndex: 1,
  },
  analysisComment: {
    name: 'Analysis comment',
    propertyKey: 'analysisComment',
    sortIndex: 2,
  },
  container: {
    name: 'Container',
    propertyKey: 'container',
    sortIndex: 3,
  },
  dilution: {
    name: 'Dilution factor',
    propertyKey: 'dilution',
    sortIndex: 4,
  },
  testDefinitionName: {
    name: 'Test definition',
    propertyKey: 'testDefinitionName',
    sortIndex: 5,
  },
  assay: {
    name: 'Assay',
    propertyKey: 'assay',
    sortIndex: 6,
  },
  worksheet: {
    name: 'Worksheet',
    propertyKey: 'worksheet',
    sortIndex: 7,
  },
};

export const TEST_ORDERS_VIEW_TABLE_TEST_ORDER_FRAGMENT = gql`
  fragment TestOrdersViewTableTestOrderFragment on TestOrderNode {
    id
    name
    status
    analysisComment
    dilutionFactor
    container {
      ... on ContainerNode {
        id
        name
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
    testDefinitionCorrect {
      ... on TestDefinitionNode {
        id
        name
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
    procedure {
      ... on ProcedureNode {
        id
        name
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
    result {
      ... on ResultNode {
        id
        tagsJson
        schema {
          ... on ResultSchemaNode {
            id
            name
            fieldsJson
          }
        }
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
    procedureMethodExecutionInstance {
      ... on ProcedureMethodExecutionInstanceNode {
        id
        worksheet {
          ... on WorksheetNode {
            id
            name
          }
          ...InaccessibleInfoGraphQL
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
`;

function getHeaderColumn(
  sortStateIndex: number,
  headers: {[index: string]: HeaderInfo}
): HeaderInfo | undefined {
  return Object.values(headers).find((column) => column.sortIndex === sortStateIndex);
}

interface TestOrderRowData {
  name: JSX.Element;
  testDefinitionName: JSX.Element;
  status: JSX.Element;
  analysisComment: JSX.Element;
  dilution: number | null;
  container: JSX.Element | null;
  assay: JSX.Element | null;
  worksheet: JSX.Element | null;
  [key: `result_${string}`]: JSX.Element | null;
  sortKeys: {
    name: string;
    testDefinitionName: string;
    status: string;
    analysisComment: string;
    dilution: string;
    container: string;
    assay: string;
    worksheet: string;
    [key: `result_${string}`]: string;
  };
}

function getTagDisplayName(tag: Tag): string {
  return isSchemaTag(tag) ? tag.tagSchemaField.displayName : tag.name;
}

function textValueWithTooltip(value: string): JSX.Element {
  return (
    <Tooltip text={value === '' ? null : value} placement={FloatingPlacement.TOP}>
      <div className={css(styles.truncatedText)}>{value}</div>
    </Tooltip>
  );
}

function getTestOrderRowData(testOrders: Array<TestOrderGQL>): Array<TestOrderRowData> {
  return testOrders.map((testOrder: TestOrderGQL): TestOrderRowData => {
    const testDefinitionChip = renderTestOrderTestDefinitionChip(testOrder, ChipSize.SMALL);
    const containerChip = renderTestOrderContainerChip(testOrder, ChipSize.SMALL);
    const assayChip = renderTestOrderAssayChip(testOrder, ChipSize.SMALL);
    const worksheetChip = renderTestOrderWorksheetChip(testOrder, ChipSize.SMALL);
    const getWorksheetName = (): string => {
      if (testOrder.procedureMethodExecutionInstance == null) {
        return '';
      }
      if (
        isInaccessible(testOrder.procedureMethodExecutionInstance) ||
        isInaccessible(testOrder.procedureMethodExecutionInstance.worksheet)
      ) {
        return '(No access)';
      }
      return testOrder.procedureMethodExecutionInstance.worksheet.name;
    };

    const resultColumns: Record<string, JSX.Element> = {};
    const resultSortValues: Record<string, string> = {};
    if (testOrder.result != null && !isInaccessible(testOrder.result)) {
      (testOrder.result.tagsJson as Array<Tag>).forEach((tag) => {
        const displayName = getTagDisplayName(tag);
        resultColumns[`result_${displayName}`] = (
          <TagValueViewer
            tag={tag}
            chipSize={ChipSize.MINI}
            shouldPreviewImages={true}
            showChipsAsLinks={true}
            summarize={true}
            summarizeThreshold={3}
          />
        );
        resultSortValues[`result_${displayName}`] = tag.value ?? '';
      });
    }

    return {
      name: textValueWithTooltip(testOrder.name),
      testDefinitionName: testDefinitionChip,
      status: <TestOrderStatusDisplay status={testOrder.status} />,
      analysisComment: textValueWithTooltip(testOrder.analysisComment ?? ''),
      dilution: testOrder.dilutionFactor,
      container: containerChip,
      assay: assayChip,
      worksheet: worksheetChip,
      ...resultColumns,
      sortKeys: {
        name: testOrder.name,
        testDefinitionName: !isInaccessible(testOrder.testDefinitionCorrect)
          ? testOrder.testDefinitionCorrect.name
          : '(No access)',
        status: testOrder.status,
        analysisComment: testOrder.analysisComment ?? '',
        dilution: testOrder.dilutionFactor?.toString() ?? '',
        container:
          testOrder.container != null && isInaccessible(testOrder.container)
            ? '(No access)'
            : (testOrder.container?.name ?? ''),
        assay:
          testOrder.procedure != null && isInaccessible(testOrder.procedure)
            ? '(No access)'
            : (testOrder.procedure?.name ?? ''),
        worksheet: getWorksheetName(),
        ...resultSortValues,
      },
    };
  });
}

function getSortedTestOrderRowData(
  testOrders: Array<TestOrderRowData>,
  headers: {[index: string]: HeaderInfo},
  sortState: Exclude<SimpleTableSortState, null>
): Array<TestOrderRowData> {
  let sortedItems = testOrders;
  const headerColumn = getHeaderColumn(sortState.columnKey as number, headers);
  if (headerColumn) {
    sortedItems = sortedBy(testOrders, [
      {
        accessor: (row) => row.sortKeys[headerColumn.propertyKey] ?? '', // Result schema field columns are optional
        naturalSort: true,
        reversed: sortState.sortDirection === 'arrow-up',
      },
    ]);
  }

  return sortedItems;
}

function getResultColumnHeaders(testOrders: Array<TestOrderGQL>): {[index: string]: HeaderInfo} {
  const resultColumnNames = testOrders.flatMap((testOrder) =>
    testOrder.result == null || isInaccessible(testOrder.result)
      ? []
      : (testOrder.result.tagsJson as Array<Tag>).map((tag) => getTagDisplayName(tag))
  );

  const maxDefaultHeaderSortIndex =
    Math.max(...Object.values(HEADER_COLUMNS).map((column) => column.sortIndex)) + 1;

  const resultsColumnHeadersDict: {[index: string]: HeaderInfo} = {};

  resultColumnNames.forEach((columnName, index) => {
    const key: `result_${string}` = `result_${columnName}`;
    resultsColumnHeadersDict[key] = {
      name: columnName,
      propertyKey: key,
      sortIndex: maxDefaultHeaderSortIndex + index,
    };
  });

  return resultsColumnHeadersDict;
}

export interface TestOrdersViewTableProps {
  testOrders: Array<TestOrderGQL>;
  itemId: string;
  studyIds: Array<string>;
  allowCreateTestOrders: boolean;
}

export function TestOrdersViewTable(props: TestOrdersViewTableProps): JSX.Element {
  const [pageOffset, setOffset] = useState<number>(0);

  const allRowData = getTestOrderRowData(props.testOrders);
  const {sortState, getHeaderCellProps} = useSimpleTableSortState(props.itemId);

  const resultHeaderColumns = getResultColumnHeaders(props.testOrders);
  const combinedHeaderColumns = {
    ...HEADER_COLUMNS,
    ...resultHeaderColumns,
  };
  const rowData = getSortedTestOrderRowData(
    allRowData,
    combinedHeaderColumns,
    sortState ?? {
      columnKey: HEADER_COLUMNS.status.sortIndex,
      sortDirection: 'arrow-up',
    }
  );

  function renderHeader(headerInfo: HeaderInfo): ReactElement<CellProps> {
    assert(headerInfo.sortIndex !== NOT_SORTABLE, 'Expected header to have sortable index');

    return (
      <SortableHeaderCell {...getHeaderCellProps(headerInfo.sortIndex)}>{headerInfo.name}</SortableHeaderCell>
    );
  }

  const paginationLimit = 100;

  return (
    <>
      <div className={css(styles.testOrderToolbarWrapper)}>
        {props.studyIds.length > 0 && props.allowCreateTestOrders && (
          <CreateTestOrdersButton sampleIds={[props.itemId]} studyIds={props.studyIds} />
        )}
      </div>
      <Table
        emptyTableText="No test orders were found."
        numRows={Math.min(rowData.length - pageOffset, paginationLimit)}
        pagination={{offset: pageOffset, limit: paginationLimit, total: rowData.length}}
        setOffset={setOffset}
        isCompact={false}
        frozenHeader={false}
        overflowVisible={false}
        showBorder={false}
      >
        {Object.values(combinedHeaderColumns).map((headerInfo) => (
          <Column
            key={headerInfo.propertyKey}
            header={renderHeader(headerInfo)}
            renderCell={(rowIndex) => (
              <Cell className={css(styles.cell)}>
                {rowData[pageOffset + rowIndex][headerInfo.propertyKey]}
              </Cell>
            )}
          />
        ))}
      </Table>
    </>
  );
}

const styles = StyleSheet.create({
  testOrderToolbarWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
  },
  cell: {
    maxWidth: '200px',
  },
  truncatedText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
});
